import React, { useEffect, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Activity, CheckCircle2, AlertCircle, XCircle, Loader2, RefreshCw } from "lucide-react";
import { BRAND } from "@/config/brand";

// Sprint 5a CoS-Decision (27.06.2026) — Public Status-Page.
// Live-Monitoring für Launch-Tag: Mail-Delivery, DOI-Confirms, Auto-Login.
// Keine PII, kein Auth nötig — öffentlich erreichbar unter /status.

const STATUS_STYLE = {
  ok: {
    icon: CheckCircle2,
    label: "Operational",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
    dot: "bg-emerald-500",
  },
  warn: {
    icon: AlertCircle,
    label: "Degraded",
    badge: "bg-amber-50 text-amber-700 border-amber-200",
    dot: "bg-amber-500",
  },
  fail: {
    icon: XCircle,
    label: "Disrupted",
    badge: "bg-rose-50 text-rose-700 border-rose-200",
    dot: "bg-rose-500",
  },
  idle: {
    icon: Activity,
    label: "Idle",
    badge: "bg-slate-50 text-slate-600 border-slate-200",
    dot: "bg-slate-400",
  },
};

function StatusCard({ component }) {
  const style = STATUS_STYLE[component.status] || STATUS_STYLE.idle;
  const Icon = style.icon;
  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:border-slate-300 hover:shadow-sm"
      data-testid={`status-card-${component.key}`}
    >
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500">
            {component.label}
          </p>
          <p
            className="text-3xl font-extrabold tracking-tight text-slate-900"
            data-testid={`status-card-${component.key}-value`}
          >
            {component.value}
          </p>
          {component.rate_percent != null && (
            <p className="text-sm font-semibold text-slate-600">
              {component.rate_percent}%
            </p>
          )}
        </div>
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${style.badge}`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${style.dot} animate-pulse`} />
          {style.label}
        </span>
      </div>
      <Icon className="w-5 h-5 text-slate-400 mt-3" />
    </div>
  );
}

export default function StatusPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const fetchStatus = async () => {
    try {
      setError(null);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/status/health`
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
      setLastFetched(new Date());
    } catch (err) {
      setError(err.message || "Status konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // Auto-refresh alle 30 Sekunden
    const id = setInterval(fetchStatus, 30000);
    return () => clearInterval(id);
  }, []);

  const components = data?.components || [];
  const overallStatus = components.length
    ? components.every((c) => c.status === "ok" || c.status === "idle")
      ? "ok"
      : components.some((c) => c.status === "fail")
      ? "fail"
      : "warn"
    : "idle";
  const overallStyle = STATUS_STYLE[overallStatus];

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-5xl mx-auto px-6 lg:px-8 py-16">
        <header className="text-center max-w-2xl mx-auto" data-testid="status-header">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-bold uppercase tracking-widest">
            <Activity className="w-3.5 h-3.5" /> System-Status
          </div>
          <h1
            className="mt-6 text-4xl sm:text-5xl font-extrabold tracking-tighter text-slate-900 leading-tight"
            data-testid="status-headline"
          >
            {BRAND.name} läuft.
          </h1>
          <p className="mt-3 text-base text-slate-600">
            Live-Übersicht über Anmeldungen, Mail-Versand und Auto-Login.
            Aktualisiert sich alle 30 Sekunden automatisch.
          </p>

          {!loading && (
            <div className="mt-8 inline-flex items-center gap-2">
              <span
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border ${overallStyle.badge}`}
                data-testid="status-overall-badge"
              >
                <span
                  className={`w-2 h-2 rounded-full ${overallStyle.dot} animate-pulse`}
                />
                Alle Systeme: {overallStyle.label}
              </span>
            </div>
          )}
        </header>

        {loading && (
          <div className="mt-16 flex justify-center" data-testid="status-loading">
            <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
          </div>
        )}

        {error && !loading && (
          <div
            className="mt-12 rounded-xl border border-rose-200 bg-rose-50 p-6 text-center"
            data-testid="status-error"
          >
            <XCircle className="w-6 h-6 text-rose-500 mx-auto" />
            <p className="mt-2 text-sm font-semibold text-rose-700">
              Status konnte nicht geladen werden.
            </p>
            <p className="mt-1 text-xs text-rose-600">{error}</p>
            <button
              type="button"
              onClick={fetchStatus}
              data-testid="status-retry"
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold"
            >
              <RefreshCw className="w-4 h-4" /> Erneut versuchen
            </button>
          </div>
        )}

        {!loading && !error && (
          <div
            className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4"
            data-testid="status-grid"
          >
            {components.map((c) => (
              <StatusCard key={c.key} component={c} />
            ))}
          </div>
        )}

        {lastFetched && !error && (
          <p className="mt-10 text-center text-xs text-slate-400" data-testid="status-last-updated">
            Zuletzt aktualisiert:{" "}
            {lastFetched.toLocaleTimeString("de-DE", {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })}
            {" · "}
            <button
              type="button"
              onClick={fetchStatus}
              className="inline-flex items-center gap-1 underline hover:text-slate-600"
              data-testid="status-refresh-btn"
            >
              <RefreshCw className="w-3 h-3" /> Jetzt aktualisieren
            </button>
          </p>
        )}
      </main>
      <Footer />
    </div>
  );
}
